
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";

// Define proper types for our Edge Function response
interface NewsletterResponse {
  success: boolean;
  message: string;
}

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation simple de l'email côté client
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email.trim())) {
      toast.error("Veuillez entrer une adresse email valide");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log("Envoi de la demande d'inscription newsletter pour:", email);
      
      // Configuration d'un timeout pour éviter les attentes trop longues
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error("La demande a pris trop de temps. Veuillez réessayer."));
        }, 15000); // 15 secondes timeout
      });
      
      // Appel de la fonction edge
      const responsePromise = supabase.functions.invoke("newsletter-signup", {
        body: { email: email.trim() }
      });
      
      // Utilisation de Promise.race pour gérer le timeout
      const result = await Promise.race([responsePromise, timeoutPromise]);
      
      // Traitement de la réponse avec vérification de type
      if (result && result.data) {
        const responseData = result.data as NewsletterResponse;
        
        if (responseData.success) {
          toast.success("Merci pour votre inscription à notre newsletter !");
          setEmail("");
        } else {
          toast.info(responseData.message || "Information concernant votre inscription");
        }
      } else if (result && result.error) {
        const error = result.error;
        
        // Gestion des erreurs connues de Supabase
        if (typeof error === 'object' && error !== null && 'message' in error) {
          const errorMessage = String(error.message || '');
          if (errorMessage.includes("déjà inscrit")) {
            toast.info("Cet email est déjà inscrit à notre newsletter.");
          } else {
            throw error;
          }
        } else {
          throw new Error("Une erreur inconnue est survenue");
        }
      }
    } catch (error: any) {
      console.error("Erreur d'inscription à la newsletter:", error);
      
      // Message d'erreur personnalisé
      let message = "Une erreur est survenue lors de l'inscription. Veuillez réessayer.";
      
      if (error && typeof error === 'object' && 'message' in error) {
        message = String(error.message);
      }
      
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <Input
        type="email"
        placeholder="Votre adresse email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="bg-white border-gray-300 text-gray-800 placeholder:text-gray-500"
        required
      />
      <Button 
        type="submit" 
        disabled={isSubmitting}
        className="w-full bg-villa-turquoise hover:bg-opacity-90"
      >
        {isSubmitting ? "Inscription en cours..." : "S'inscrire"}
      </Button>
    </form>
  );
};

export default NewsletterForm;
